import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Search, CreditCard, Star, Shield, TrendingUp, MessageSquare, Calendar, BarChart2, CheckCircle } from 'lucide-react';

const HowItWorksPage = () => {
  const guestSteps = [
    {
      step: '01',
      icon: <Search size={28} className="text-[#1F3A68]" />,
      title: 'Search & Filter',
      desc: 'Browse short lets across the UK. Filter by location, price, dates, amenities, and property type — from city apartments to countryside retreats.',
    },
    {
      step: '02',
      icon: <Home size={28} className="text-[#1F3A68]" />,
      title: 'View & Compare',
      desc: 'Each listing includes verified photos, detailed amenity lists, neighbourhood info, and transparent pricing with no hidden fees.',
    },
    {
      step: '03',
      icon: <CreditCard size={28} className="text-[#1F3A68]" />,
      title: 'Book Securely',
      desc: 'Instant or request-to-book options available. All payments are held securely until 24 hours after check-in, protecting you throughout your stay.',
    },
    {
      step: '04',
      icon: <Star size={28} className="text-[#1F3A68]" />,
      title: 'Stay & Review',
      desc: 'Enjoy your stay with 24/7 guest support. After checkout, share your honest review to help other travellers and reward great hosts.',
    },
  ];

  const hostSteps = [
    {
      step: '01',
      icon: <Home size={28} className="text-white" />,
      title: 'List Your Property',
      desc: 'Create your listing in minutes with our guided setup. Add photos, set your amenities, house rules, and pricing — we\'ll walk you through every step.',
    },
    {
      step: '02',
      icon: <TrendingUp size={28} className="text-white" />,
      title: 'Optimise with Data',
      desc: 'Our pricing intelligence analyses local demand, competitor rates, and seasonal trends to recommend the rates that maximise your revenue.',
    },
    {
      step: '03',
      icon: <Calendar size={28} className="text-white" />,
      title: 'Manage with Ease',
      desc: 'One dashboard for bookings, messages, payments, and reviews. Our automated tools handle the admin so you can focus on scaling.',
    },
    {
      step: '04',
      icon: <BarChart2 size={28} className="text-white" />,
      title: 'Grow Your Portfolio',
      desc: 'Use our Deal Marketplace to find R2R landlord opportunities, track performance with detailed analytics, and expand your property business.',
    },
  ];

  const features = [
    { icon: <Shield size={22} className="text-[#1F3A68]" />, title: 'Reviewed listings', desc: 'All property photos are reviewed before going live.' },
    { icon: <MessageSquare size={22} className="text-[#1F3A68]" />, title: 'Secure messaging', desc: 'Chat directly with hosts or guests — securely in-platform.' },
    { icon: <CreditCard size={22} className="text-[#1F3A68]" />, title: 'Protected payments', desc: 'Funds held securely; released on successful check-in.' },
    { icon: <Star size={22} className="text-[#1F3A68]" />, title: 'Transparent reviews', desc: 'Honest, unedited reviews from verified guests.' },
    { icon: <TrendingUp size={22} className="text-[#1F3A68]" />, title: 'Smart pricing', desc: 'AI-powered pricing recommendations updated daily.' },
    { icon: <CheckCircle size={22} className="text-[#1F3A68]" />, title: 'Dispute resolution', desc: '24/7 support team to resolve any issues quickly.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div></Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Browse</Link>
            <Link to="/become-host" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Become a Host</Link>
            <Link to="/login" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#1F3A68] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-5">How Ariv works</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            A smarter way to book extraordinary stays — and a more powerful way to build a profitable hosting business.
          </p>
          <div className="flex justify-center gap-4 mt-10">
            <a href="#guests" className="bg-white text-[#1F3A68] px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors">For Guests</a>
            <a href="#hosts" className="border-2 border-white/50 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors">For Hosts</a>
          </div>
        </div>
      </section>

      {/* Guest section */}
      <section id="guests" className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-block bg-[#1F3A68]/10 text-[#1F3A68] px-4 py-1.5 rounded-full text-sm font-semibold mb-4">FOR GUESTS</div>
          <h2 className="text-4xl font-bold text-[#1F3A68]">Book your perfect stay in 4 steps</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {guestSteps.map((s, i) => (
            <div key={i} className="relative">
              <div className="bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow h-full">
                <div className="text-5xl font-black text-gray-100 mb-4 leading-none">{s.step}</div>
                <div className="mb-4">{s.icon}</div>
                <h3 className="font-bold text-[#1F3A68] text-lg mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
              {i < guestSteps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <ArrowRight size={20} className="text-[#1F3A68]/30" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/" className="inline-flex items-center gap-2 bg-[#1F3A68] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1F3A68]/90 transition-colors">
            Browse Properties
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Host section */}
      <section id="hosts" className="bg-[#1F3A68] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-block bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">FOR HOSTS</div>
            <h2 className="text-4xl font-bold text-white">Build a professional hosting business</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hostSteps.map((s, i) => (
              <div key={i} className="relative">
                <div className="bg-white/10 backdrop-blur rounded-2xl p-7 hover:bg-white/15 transition-colors h-full">
                  <div className="text-5xl font-black text-white/20 mb-4 leading-none">{s.step}</div>
                  <div className="mb-4">{s.icon}</div>
                  <h3 className="font-bold text-white text-lg mb-2">{s.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{s.desc}</p>
                </div>
                {i < hostSteps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight size={20} className="text-white/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/become-host" className="inline-flex items-center gap-2 bg-white text-[#1F3A68] px-8 py-4 rounded-xl font-bold hover:bg-gray-50 transition-colors">
              Start Hosting
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Platform features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-[#1F3A68]">Built on trust and transparency</h2>
            <p className="text-gray-600 mt-3 text-lg">Features that protect everyone on the platform</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 flex items-start gap-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="bg-[#1F3A68]/10 rounded-xl p-3 flex-shrink-0">{f.icon}</div>
                <div>
                  <h3 className="font-bold text-[#1F3A68] mb-1">{f.title}</h3>
                  <p className="text-gray-600 text-sm">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="bg-[#1F3A68] rounded-3xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
          <p className="text-white/70 text-lg mb-8">Our Help Center has answers to everything. Or get in touch with our team directly.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/help" className="bg-white text-[#1F3A68] px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors">
              Visit Help Center
            </Link>
            <Link to="/contact" className="border-2 border-white/50 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-[23px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[23px] w-auto object-contain brightness-0 invert" /></div>
          <div className="flex gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/careers" className="hover:text-white">Careers</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ariv. A product of Brick & Capital Ltd.</p>
          <p className="text-white/40 text-xs mt-2"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/70">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
